import { get } from 'svelte/store'
import { t } from 'svelte-i18n'
import type { ResourceSchema } from '@semanticedge/persistence-service-noheaders-client/src'
import { ButtonType, ToastType } from '@repo/commons/components'
import type { ReferenceTableRow, TableData } from '@repo/commons/models'
import { confirmationStore, toastsStore } from '@repo/commons/store'
import {
	injectTranslation,
	showErrorToast,
	showSaveFailedToast,
	showToast
} from '@repo/commons/utils'
import { validateReferenceTableCsvFile } from './referenceTableCsv.js'

export const getTableRowId = ({ id }: TableData): string => String(id)

const confirmCsvImport = (currentRowCount: number, incomingRowCount: number): Promise<boolean> => {
	const translate = get(t)
	const incoming = String(incomingRowCount)
	const message =
		currentRowCount === 0
			? injectTranslation('ReferenceTableRowsView.importConfirmMessageEmpty', { incoming })
			: injectTranslation('ReferenceTableRowsView.importConfirmMessage', {
					count: String(currentRowCount),
					incoming
				})

	return confirmationStore.show({
		title: translate('ReferenceTableRowsView.importConfirmTitle'),
		message,
		confirmButtonType: ButtonType.BRAND,
		confirmText: translate('ReferenceTableRowsView.importCsv'),
		cancelText: translate('FORM.cancel')
	})
}

type CsvImportRequest = {
	file: File
	schema: ResourceSchema | undefined
	currentRowCount: number
	onImport: (rows: ReferenceTableRow[]) => Promise<boolean>
}

export const importReferenceTableCsv = async ({
	file,
	schema,
	currentRowCount,
	onImport
}: CsvImportRequest): Promise<void> => {
	const translate = get(t)

	if (!schema) {
		showErrorToast(translate('ReferenceTableForm.noSchemasHint'))

		return
	}

	const { data = [], errors } = await validateReferenceTableCsvFile(file, schema)

	if (errors.length > 0) {
		showErrorToast(errors.flatMap(({ errors: groupErrors }) => groupErrors).join('\n'))

		return
	}

	if (data.length === 0) {
		showErrorToast(translate('ReferenceTableRowsView.importEmpty'))

		return
	}

	if (!(await confirmCsvImport(currentRowCount, data.length))) return

	if (!(await onImport(data))) {
		showSaveFailedToast()

		return
	}

	showToast(toastsStore, {
		type: ToastType.SUCCESS,
		message: translate('ReferenceTableRowsView.importSuccess')
	})
}
